import api from "@/lib/api";
import { isStudioPreview, previewQueryParams } from "@/lib/studioPreviewBridge";
import { getLang, langParams } from "@/lib/lang";
import { DEFAULT_ENGINE } from "@/themes/registry";
import { bootstrapSections } from "@/lib/bootstrap";

// Chargement des sections d'un template — SOURCE UNIQUE pour les trois
// endroits qui en ont besoin : Home.jsx (accueil), useTemplateSections.js
// (en-tête, pied de page) et TemplateSections.jsx (produit, collection,
// panier).
//
// Chacun avait auparavant son propre cache de module, ce qui produisait deux
// défauts mesurés sur le site en production :
//
//   * des appels EN DOUBLE. Deux composants qui demandent le même template au
//     même instant (Layout.jsx et Footer.jsx demandent tous deux "footer")
//     voyaient tous deux un cache vide et lançaient chacun leur requête. Le
//     cache ne se remplit qu'à la réponse, donc il n'aide pas contre les
//     appels simultanés — seul le suivi des requêtes EN VOL le fait ;
//   * aucun partage entre les trois modules : l'accueil chargé par Home.jsx
//     était rechargé si TemplateSections.jsx demandait "home".
//
// Deux tables, et c'est la seconde qui règle le problème d'origine :
// `_resolved` garde les réponses déjà obtenues, `_inflight` garde les
// requêtes en cours pour que les appelants suivants s'y raccrochent au lieu
// d'en lancer une nouvelle.
const _resolved = new Map();
const _inflight = new Map();

// La langue fait partie de la clé : la même page dans deux langues n'est pas
// la même réponse (module multilingue).
function cacheKey(template) {
  return `${template}|${getLang() || ""}`;
}

// Amorçage serveur (lib/bootstrap.js) : les sections d'en-tête / pied de
// page (et de l'accueil) sont connues avant le premier rendu — aucun appel
// réseau, aucun flash du chrome par défaut.
Object.entries(bootstrapSections() || {}).forEach(([template, entry]) => {
  if (Array.isArray(entry?.sections)) {
    _resolved.set(cacheKey(template), { sections: entry.sections, engine: entry.engine || DEFAULT_ENGINE });
  }
});

/**
 * Lecture SYNCHRONE du cache, ou `undefined` si rien n'est encore connu.
 * Permet un premier rendu sans aller-retour réseau ni écran de chargement
 * lors d'une navigation interne.
 */
export function peekTemplateSections(template) {
  if (isStudioPreview()) return undefined;
  return _resolved.get(cacheKey(template));
}

/**
 * Charge les sections d'un template. Ne rejette JAMAIS : un échec réseau
 * résout avec `sections: null`, que les appelants interprètent comme
 * « API indisponible, afficher le repli » — une page reste lisible même
 * quand le serveur ne répond pas.
 *
 * En prévisualisation Studio, ni cache ni déduplication : le brouillon
 * change à chaque frappe de l'admin.
 */
export function fetchTemplateSections(template) {
  const preview = isStudioPreview();
  const key = cacheKey(template);

  if (!preview) {
    const hit = _resolved.get(key);
    if (hit) return Promise.resolve(hit);
    const pending = _inflight.get(key);
    if (pending) return pending;
  }

  const request = api
    .get("/theme/sections", { params: { template, ...langParams(), ...previewQueryParams() } })
    .then(({ data }) => ({
      sections: Array.isArray(data?.sections) ? data.sections : [],
      engine: data?.engine || DEFAULT_ENGINE,
    }))
    .catch(() => ({ sections: null, engine: DEFAULT_ENGINE }))
    .then((result) => {
      if (!preview) {
        _resolved.set(key, result);
        _inflight.delete(key);
      }
      return result;
    });

  if (!preview) _inflight.set(key, request);
  return request;
}

/**
 * Remplace le contenu en cache pour un template — utilisé par la
 * prévisualisation Studio, où le brouillon arrive par postMessage et doit
 * primer sur ce que l'API a renvoyé.
 */
export function primeTemplateSections(template, sections, engine) {
  _resolved.set(cacheKey(template), { sections, engine: engine || DEFAULT_ENGINE });
}
